import {
    Error as ErrorIcon,
    Event as EventIcon,
    Info as InfoIcon,
    Notifications as NotificationIcon,
    Psychology as PsychologyIcon,
    School as SchoolIcon,
    Computer as SystemIcon,
    Warning as WarningIcon,
    WorkOutline as WorkOutlineIcon,
} from '@mui/icons-material';
import { Box, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mockMessageService } from '../services/MockMessageService';
import { EMessageType, type IMessage } from '../types/IMessage';

export function SystemTab() {
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    useEffect(() => {
        loadMessages();
    }, []);

    const loadMessages = async () => {
        try {
            const systemMessages = await mockMessageService.getSystemMessages();
            setMessages(systemMessages);
        } catch (error) {
            console.error('Error loading system messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMessageClick = async (message: IMessage) => {
        if (!message.read) {
            await mockMessageService.markAsRead(message.id);
            setMessages((prev) => prev.map((msg) => (msg.id === message.id ? { ...msg, read: true } : msg)));
        }
    };

    const getMessageIcon = (message: IMessage) => {
        if (message.type === EMessageType.SYSTEM) {
            switch (message.priority) {
                case 'high':
                    return <ErrorIcon color="error" />;
                case 'normal':
                    return <SystemIcon color="info" />;
                default:
                    return <InfoIcon color="info" />;
            }
        }
        // For NOTIFICATION type - use skill color for skill suggestions
        if (message.subject.toLowerCase().includes('skill')) {
            return <PsychologyIcon sx={{ color: '#4dcea9' }} />;
        }
        if (message.subject.toLowerCase().includes('certificate')) {
            return <SchoolIcon color="secondary" />;
        }
        if (message.subject.toLowerCase().includes('meeting')) {
            return <EventIcon color="warning" />;
        }
        if (message.subject.toLowerCase().includes('project')) {
            return <WorkOutlineIcon color="info" />;
        }
        return <PsychologyIcon sx={{ color: '#4dcea9' }} />;
    };

    const getPriorityChip = (priority?: string) => {
        switch (priority) {
            case 'high':
                return <Chip label={langStrings.messages.important} size="small" color="error" variant="outlined" />;
            case 'low':
                return <Chip label={langStrings.messages.low} size="small" color="default" variant="outlined" />;
            default:
                return null;
        }
    };

    const formatTimestamp = (timestamp: Date) => {
        const now = new Date();
        const diff = now.getTime() - new Date(timestamp).getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} ${langStrings.messages.daysAgo}`;
        } else if (hours > 0) {
            return `${hours} ${langStrings.messages.hoursAgo}`;
        } else {
            return langStrings.messages.justNow;
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>{langStrings.messages.loadingSystemMessages}</Typography>
            </Box>
        );
    }

    if (messages.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">{langStrings.messages.noSystemMessages}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={1}>
                <List sx={{ p: 0 }}>
                    {messages.map((message, index) => (
                        <React.Fragment key={message.id}>
                            <ListItem
                                component="div"
                                onClick={() => handleMessageClick(message)}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: message.read ? 'transparent' : 'action.hover',
                                    '&:hover': {
                                        bgcolor: 'action.selected',
                                    },
                                }}
                            >
                                <ListItemIcon>{getMessageIcon(message)}</ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: message.read ? 400 : 600,
                                                    flexGrow: 1,
                                                }}
                                            >
                                                {message.subject}
                                            </Typography>
                                            {getPriorityChip(message.priority)}
                                            {!message.read && (
                                                <Chip
                                                    label={langStrings.messages.new}
                                                    size="small"
                                                    color="primary"
                                                    variant="filled"
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {message.content}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {message.type === EMessageType.SYSTEM
                                                    ? langStrings.messages.system
                                                    : langStrings.messages.notification}{' '}
                                                • {formatTimestamp(message.timestamp)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            {index < messages.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
        </Box>
    );
}
